import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, AlertCircle } from 'lucide-react';

const Step2Username = ({ username, onUpdate, onNext, onBack }) => {
  const [inputUsername, setInputUsername] = useState(username);
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    setInputUsername(username);
  }, [username]);

  const validateUsername = (value) => {
    const usernameRegex = /^[a-zA-Z0-9_-]{3,20}$/;
    return usernameRegex.test(value);
  };

  const handleUsernameChange = (e) => {
    const value = e.target.value.toLowerCase();
    setInputUsername(value);
    setError('');
    
    if (value.length >= 3) {
      setIsValid(validateUsername(value));
    } else {
      setIsValid(false);
    }
  };

  const handleContinue = () => {
    if (isValid && inputUsername) {
      onUpdate({ username: inputUsername });
      onNext();
    }
  };

  const generateSuggestions = () => {
    const suggestions = ['creative', 'artist', 'designer', 'writer', 'musician', 'photographer', 'developer', 'influencer'];
    return suggestions.filter(s => s.includes(inputUsername.toLowerCase()) && s !== inputUsername.toLowerCase());
  };

  return (
    <StepContainer>
      <StepCard>
        <StepHeader>
          <StepTitle>Pick a username</StepTitle>
          <StepSubtitle>What would you like your link to be?</StepSubtitle>
        </StepHeader>

        <UsernameInputContainer>
          <InputWrapper>
            <InputPrefix>bucx.io/</InputPrefix>
            <UsernameInput
              type="text"
              value={inputUsername}
              onChange={handleUsernameChange}
              placeholder="username"
              $isValid={isValid}
              $hasError={error}
            />
          </InputWrapper>
          
          {inputUsername && (
            <ValidationMessage $isValid={isValid} $hasError={error}>
              {error ? (
                <ErrorText><AlertCircle size={16} />{error}</ErrorText>
              ) : isValid ? (
                <ValidText><Check size={16} />Username is available!</ValidText>
              ) : (
                <InvalidText><AlertCircle size={16} />Username must be 3-20 characters, letters, numbers, hyphens, and underscores only</InvalidText>
              )}
            </ValidationMessage>
          )}
        </UsernameInputContainer>

        {inputUsername && isValid && (
          <PreviewContainer>
            <PreviewTitle>Your page preview:</PreviewTitle>
            <PagePreview>
              <PreviewHeader>
                <PreviewAvatar>
                  <PreviewAvatarIcon>👤</PreviewAvatarIcon>
                </PreviewAvatar>
                <PreviewInfo>
                  <PreviewName>Your Display Name</PreviewName>
                  <PreviewUrl>bucx.io/{inputUsername}</PreviewUrl>
                </PreviewInfo>
              </PreviewHeader>
              <PreviewContent>
                 <PreviewBio>Your bio will appear here...</PreviewBio>
                <PreviewStats>
                  <StatItem>
                    <StatNumber>0</StatNumber>
                    <StatLabel>Supporters</StatLabel>
                  </StatItem>
                  <StatItem>
                    <StatNumber>0</StatNumber>
                    <StatLabel>Posts</StatLabel>
                  </StatItem>
                </PreviewStats>
              </PreviewContent>
            </PagePreview>
          </PreviewContainer>
        )}

        {inputUsername && !isValid && inputUsername.length >= 3 && (
          <SuggestionsContainer>
            <SuggestionsTitle>Try these usernames instead:</SuggestionsTitle>
            <SuggestionsList>
              {generateSuggestions().slice(0, 3).map((suggestion) => (
                <SuggestionItem
                  key={suggestion}
                  onClick={() => {
                    setInputUsername(suggestion);
                    setIsValid(true);
                    setError('');
                  }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  bucx.io/{suggestion}
                </SuggestionItem>
              ))}
            </SuggestionsList>
          </SuggestionsContainer>
        )}

        <TipContainer>
          <TipIcon>💡</TipIcon>
          <TipText>You can change it anytime!</TipText>
        </TipContainer>

        <ButtonContainer>
          <BackButton onClick={onBack} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <ArrowLeft size={20} /> Back
          </BackButton>
          
          <NextButton
            onClick={handleContinue}
            disabled={!isValid || !inputUsername}
            $disabled={!isValid || !inputUsername}
            whileHover={{ scale: isValid && inputUsername ? 1.05 : 1 }}
            whileTap={{ scale: isValid && inputUsername ? 0.95 : 1 }}
          >
            Next
          </NextButton>
        </ButtonContainer>
      </StepCard>
    </StepContainer>
  );
};

const StepContainer = styled.div`
  width: 100%;
  max-width: 700px;
  display: flex;
  justify-content: center;
`;

const StepCard = styled.div`
  background: #111111;
  border: 1px solid #333333;
  border-radius: 20px;
  padding: 3rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  width: 100%;
`;

const StepHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const StepTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: bold;
  font-family: var(--font-heading);
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const StepSubtitle = styled.p`
  font-size: 1.1rem;
  font-family: var(--font-body);
  color: #cccccc;
  margin: 0;
`;

const UsernameInputContainer = styled.div`
  margin-bottom: 2rem;
`;

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  border: 2px solid #333333;
  border-radius: 12px;
  padding: 0.5rem;
  background: #1a1a1a;
  transition: all 0.3s ease;
  
  &:focus-within {
    border-color: #e0c16f;
    background: #000000;
  }
`;

const InputPrefix = styled.span`
  font-size: 1.1rem;
  color: #cccccc;
  padding: 0 1rem;
  font-weight: 500;
  font-family: var(--font-body);
`;

const UsernameInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 1.1rem;
  padding: 1rem;
  background: transparent;
  color: #ffffff;
  font-weight: 500;
  font-family: var(--font-body);
  
  &::placeholder {
    color: #666666;
  }
`;

const ValidationMessage = styled.div`
  margin-top: 1rem;
  padding: 1rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  
  background: ${props => 
    props.$hasError ? 'rgba(255, 107, 107, 0.1)' : 
    props.$isValid ? 'rgba(40, 167, 69, 0.1)' : 'rgba(224, 193, 111, 0.1)'
  };
  color: ${props => 
    props.$hasError ? '#ff6b6b' : 
    props.$isValid ? '#4caf50' : '#e0c16f'
  };
`;

const ErrorText = styled.span`display: flex; align-items: center; gap: 0.5rem;`;
const ValidText = styled.span`display: flex; align-items: center; gap: 0.5rem;`;
const InvalidText = styled.span`display: flex; align-items: center; gap: 0.5rem;`;

const PreviewContainer = styled.div`margin-bottom: 2rem;`;

const PreviewTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  font-family: var(--font-heading);
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const PagePreview = styled.div`
  border: 2px solid #333333;
  border-radius: 15px;
  padding: 1.5rem;
  background: #1a1a1a;
`;

const PreviewHeader = styled.div`display: flex; align-items: center; margin-bottom: 1rem;`;

const PreviewAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
`;

const PreviewAvatarIcon = styled.span`font-size: 1.5rem;`;

const PreviewInfo = styled.div`flex: 1;`;

const PreviewName = styled.h4`
  font-size: 1.2rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 0.25rem 0;
`;

const PreviewUrl = styled.p`
  font-size: 0.9rem;
  color: #e0c16f;
  margin: 0;
`;

const PreviewContent = styled.div``;

const PreviewBio = styled.p`
  font-size: 0.9rem;
  color: #cccccc;
  margin: 0 0 1rem 0;
`;

const PreviewStats = styled.div`display: flex; gap: 2rem;`;
const StatItem = styled.div`text-align: center;`;

const StatNumber = styled.div`
  font-size: 1.5rem;
  font-weight: bold;
  color: #e0c16f;
`;

const StatLabel = styled.div`
  font-size: 0.8rem;
  color: #888888;
  text-transform: uppercase;
`;

const SuggestionsContainer = styled.div`margin-bottom: 2rem;`;

const SuggestionsTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const SuggestionsList = styled.div`display: flex; flex-wrap: wrap; gap: 0.5rem;`;

const SuggestionItem = styled(motion.button)`
  padding: 0.5rem 1rem;
  background: #1a1a1a;
  color: #e0c16f;
  border: 1px solid #e0c16f;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(224, 193, 111, 0.1);
  }
`;

const TipContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  background: rgba(224, 193, 111, 0.1);
  border-radius: 8px;
  margin-bottom: 2rem;
  border: 1px solid rgba(224, 193, 111, 0.2);
`;

const TipIcon = styled.span`font-size: 1.2rem;`;

const TipText = styled.p`
  font-size: 0.9rem;
  color: #e0c16f;
  margin: 0;
`;

const ButtonContainer = styled.div`display: flex; gap: 1rem;`;

const BackButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: #1a1a1a;
  color: #cccccc;
  border: 2px solid #333333;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #2a2a2a;
    border-color: #555555;
  }
`;

const NextButton = styled(motion.button)`
  flex: 1;
  padding: 1rem 2rem;
  background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #e0c16f 5.18%, #bd851b 93.21%)'};
  color: ${props => props.$disabled ? '#666666' : '#000000'};
  border: none;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.$disabled ? '#333333' : 'linear-gradient(95.36deg, #bd851b 5.18%, #e0c16f 93.21%)'};
  }
`;

export default Step2Username;